import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import getSRTTrunkTypesForCompany from 'API/SRT/getSRTTrunkTypesForCompany';
import { SRT_TRUNK_TYPE_ID } from '@/constants/srt';

const EMPTY_TRUNK_TYPES = [];

/**
 * Trunk types available to the current company's SRT service.
 *
 * Trunk type names are backend-owned display strings, so anything that needs to
 * recognise an SRT trunk matches on id via `isSRTTrunk` rather than comparing
 * against a hardcoded name. `srtTrunkTypeID` is the type the add form creates
 * against, resolved from the company's available list so an unavailable type
 * comes back empty rather than silently creating against the wrong one.
 *
 * @param {object}  [options]
 * @param {boolean} [options.enabled] Skip the fetch when false (e.g. edit mode).
 */
const useSRTTrunkTypes = ({ enabled = true } = {}) => {
    const { currentCompany } = useSelector((state) => state.navigationLists);

    const { data, ...rest } = useQuery({
        queryKey: ['srtTrunkTypes'],
        queryFn: async () => {
            const res = await getSRTTrunkTypesForCompany(currentCompany);
            return res.data || [];
        },
        enabled: Boolean(currentCompany) && enabled,
    });

    const trunkTypes = data ?? EMPTY_TRUNK_TYPES;

    const isSRTTrunkType = useCallback(
        (trunkTypeID) =>
            Boolean(trunkTypeID) &&
            trunkTypes.some((type) => type.id === trunkTypeID),
        [trunkTypes],
    );

    const isSRTTrunk = useCallback(
        (trunk) => isSRTTrunkType(trunk?.trunkType?.id),
        [isSRTTrunkType],
    );

    const srtTrunkType =
        trunkTypes.find(({ id }) => id === SRT_TRUNK_TYPE_ID) || null;

    return {
        ...rest,
        trunkTypes,
        srtTrunkType,
        srtTrunkTypeID: srtTrunkType?.id || '',
        isSRTTrunkType,
        isSRTTrunk,
    };
};

export default useSRTTrunkTypes;
